import React, {useContext, useMemo} from "react";
import {useDispatch} from "react-redux";
import DataTable, {TableColumn} from "react-data-table-component";

import Card from "react-bootstrap/Card";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import {Tournament} from "../../models/Tournament";
import {formatFullName} from "../../util/formatFullName";
import {setActiveTournamentPlayerSummary} from "./activeTournamentSlice";
import {UserInfo} from "../../models/User";
import {isMatchStatusBye} from "../../models/Match";
import {
    classesForCrossTablePlayerSummary,
    CrossTableResult,
    crossTableResultsForRounds
} from "../../util/crossTableResults";
import {TournamentPlayer} from "../../models/TournamentPlayer";
import UsernameAndTeam from "../../components/UsernameAndTeam";
import {ActiveTournamentContext} from "./ActiveTournament";

const ratingForPlayer = (player: TournamentPlayer | undefined) => (
    player?.rating ?? "–"
);

const DisplayOpponent: React.FC<{tournament: Tournament, opponent: TournamentPlayer}> = ({tournament, opponent}) => {
    const dispatch = useDispatch();

    return (
        <>
            <Button variant="link"
                    className="p-0"
                    onClick={() => dispatch(setActiveTournamentPlayerSummary({
                        tournament,
                        player: tournament.ref_user[opponent.user_id]
                    }))}>
                {formatFullName(tournament.ref_user[opponent.user_id])}
            </Button>{" "}
            <small>({ratingForPlayer(opponent)})</small>
        </>
    );
};

interface TournamentRoundSummary {
    round: string
    result: CrossTableResult
}

const TournamentSummaryCard: React.FC<{tournament: Tournament, player: UserInfo}> = ({tournament, player} ) => {
    const playerIdToPlayer = useMemo(
        () => Object.fromEntries(tournament.players.map((player) => [player.user_id, player])),
        [tournament.players]
    );
    const crossTableResults = useMemo(
        () => crossTableResultsForRounds(tournament.rounds),
        [tournament.rounds]
    );
    const roundSummaryForPlayer = Object.keys(crossTableResults).map((round) => ({round, result: crossTableResults[parseInt(round)][player.id]}));
    const columns: TableColumn<TournamentRoundSummary>[] = [{
        selector: (row) => row.round,
        name: "Round",
        right: true
    }, {
        name: "Result",
        cell: (row) => (
            <span className={(row.result) ? classesForCrossTablePlayerSummary(row.result, row.result.colour) : ""}>
                {row.result && (isMatchStatusBye(row.result.match) ? `Bye (${row.result.result})` : row.result.result)}
            </span>
        ),
    }, {
        name: "Colour",
        cell: (row) => (row.result && row.result.opponentId !== "BYE") ? row.result.colour : ""
    }, {
        name: "Opponent",
        grow: 10,
        cell: (result) => {
            // this is messy because some of the results of auto-generated tournaments
            // can have an empty result so we check for that first
            if (!result.result) {
                return "";
            }
            const {opponentId} = result.result;
            const opponent = (opponentId === "BYE") ? opponentId : tournament.ref_user[opponentId];

            if (opponent && opponent !== "BYE") {
                return <DisplayOpponent tournament={tournament} opponent={playerIdToPlayer[opponent.id]}/>
            } else {
                 return "";
            }
        }
    }];

    return (
        <Card className="mt-3">
            <Card.Body>
                <Card.Title>
                    {tournament.name}
                </Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                    Tournament history
                </Card.Subtitle>
                <DataTable<TournamentRoundSummary> keyField="round"
                                                   data={roundSummaryForPlayer}
                                                   columns={columns}
                                                   noDataComponent="(No rounds)"/>
            </Card.Body>
        </Card>
    );
};

interface Props {
    player: UserInfo
    tournament: Tournament
    onClose: () => void
}

const ActiveTournamentPlayerSummary: React.FC<Props> = ({player, tournament, onClose}) => {
    const {onlinePlayerIds} = useContext(ActiveTournamentContext);
    const tournamentPlayer = tournament.players.find((tp) => tp.user_id === player.id);

    return (
        <Modal show={true} size="lg" onHide={() => onClose()}>
            <Modal.Header closeButton>
                <Modal.Title className="w-100">
                    <UsernameAndTeam tournament={tournament}
                                     player={tournamentPlayer}
                                     online={onlinePlayerIds?.has(player.id)}/>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/*<PlayerSummaryCard tournament={tournament} player={player}/>*/}
                <TournamentSummaryCard tournament={tournament} player={player}/>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={() => onClose()}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ActiveTournamentPlayerSummary;
